import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-bazaar-117/artifacts/2j1k3po7_Reggae%20Essences%20Logo.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totalItems } = useCart();
  const { totalItems: wishlistItems } = useWishlist();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/shop', label: 'Shop' },
    { path: '/blog', label: 'Blog' },
    { path: '/about', label: 'About' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-4 left-4 right-4 z-50 rounded-full transition-all duration-300 ${
        scrolled
          ? 'bg-cream/95 backdrop-blur-md shadow-lg border border-warm-brown/10'
          : 'bg-cream/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="Reggae Essences" className="h-10 w-auto rounded-lg" />
          <span className="hidden md:block font-heading text-lg font-semibold text-forest">
            Reggae Essences
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={`font-medium transition-colors ${
                isActive(link.path) ? 'text-forest' : 'text-warm-brown hover:text-forest'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <Link to="/wishlist" data-testid="wishlist-link" className="relative p-2 rounded-full hover:bg-forest/5 transition-colors">
            <Heart className="h-6 w-6 text-forest" />
            {wishlistItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-cream text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {wishlistItems}
              </span>
            )}
          </Link>

          <Link to="/cart" data-testid="cart-button" className="relative p-2 rounded-full hover:bg-forest/5 transition-colors">
            <ShoppingCart className="h-6 w-6 text-forest" />
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-terracotta text-cream text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </Link>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-full hover:bg-forest/5"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="h-6 w-6 text-forest" /> : <Menu className="h-6 w-6 text-forest" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 mt-2 bg-cream rounded-2xl shadow-xl p-6 mx-4 border border-warm-brown/10">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`font-medium py-2 ${isActive(link.path) ? 'text-forest' : 'text-warm-brown'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-forest text-cream py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Reggae Essences" className="h-16 w-auto rounded-lg mb-4" />
            <p className="text-cream/80 max-w-md mb-4">
              Naturally rooted. Soulfully made. Handmade care with island rhythm — 
              luxurious soaps, body butters, and candles crafted with love.
            </p>
            <p className="text-gold font-heading italic">"Island vibes for skin & soul"</p>
          </div>

          <div>
            <h4 className="font-heading text-lg font-semibold mb-4 text-gold">Shop</h4>
            <div className="flex flex-col gap-2">
              <Link to="/shop" className="text-cream/80 hover:text-cream transition-colors">All Products</Link>
              <Link to="/shop?category=soaps" className="text-cream/80 hover:text-cream transition-colors">Handmade Soaps</Link>
              <Link to="/shop?category=body_butters" className="text-cream/80 hover:text-cream transition-colors">Body Butters</Link>
              <Link to="/shop?category=candles" className="text-cream/80 hover:text-cream transition-colors">Candles</Link>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg font-semibold mb-4 text-gold">Info</h4>
            <div className="flex flex-col gap-2">
              <Link to="/about" className="text-cream/80 hover:text-cream transition-colors">About Us</Link>
              <Link to="/faq" className="text-cream/80 hover:text-cream transition-colors">FAQ</Link>
              <Link to="/contact" className="text-cream/80 hover:text-cream transition-colors">Contact</Link>
              <Link to="/track-order" className="text-cream/80 hover:text-cream transition-colors">Track Order</Link>
              <Link to="/wishlist" className="text-cream/80 hover:text-cream transition-colors">Wishlist</Link>
              <Link to="/policies/shipping" className="text-cream/80 hover:text-cream transition-colors">Shipping Policy</Link>
              <Link to="/policies/refunds" className="text-cream/80 hover:text-cream transition-colors">Refund Policy</Link>
              <Link to="/policies/privacy" className="text-cream/80 hover:text-cream transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>

        <div className="border-t border-cream/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-cream/60 text-sm">© {new Date().getFullYear()} Reggae Essences LLC. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://www.facebook.com/share/1Eww5U67cE/" target="_blank" rel="noopener noreferrer" className="text-cream/60 hover:text-gold transition-colors">Facebook</a>
            <a href="https://www.instagram.com/nadineportersoto" target="_blank" rel="noopener noreferrer" className="text-cream/60 hover:text-gold transition-colors">Instagram</a>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-cream/10">
          <p className="text-cream/50 text-xs text-center">
            All products are for external use only. Made with natural ingredients. 
            <Link to="/policies/disclaimer" className="hover:text-gold ml-1">View ingredient disclaimer</Link>
          </p>
        </div>

        <div className="mt-4 flex justify-center">
          <a href="https://www.centurionsoftwaresolutions.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 opacity-40 hover:opacity-70 transition-opacity">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/reggae-essences/images/45e7fa23-15cf-4577-ba0d-37439d209e73.png`} alt="Centurion Software Solutions" className="h-4 w-4 rounded-sm object-contain" />
            <span className="text-cream/50 text-[10px] tracking-wide">Powered by Centurion Software Solutions</span>
          </a>
        </div>
      </div>
    </footer>
  );
};
